import { removeToken, removeUser, getCsrfToken } from './auth';
import { env } from './env';
import { ApiHttpError } from './api-error';
import type { BusinessInput, ReviewListParams } from '../types/api';

export const normalizePlatformSlug = (value?: string | null) => {
  if (value === undefined || value === null) {
    return '';
  }
  return value.toString().trim().toLowerCase();
};

const API_BASE_URL = env.apiBaseUrl;

type ApiResponse<T> = T;

interface ApiErrorBody {
  message?: string;
  errorCode?: string;
  correlationId?: string;
}

/**
 * The QuotaExceededException shape (RevomiraException-derived, distinct from BE-004's unified
 * shape) still uses `error` rather than `message` -- see GlobalExceptionFilter's dedicated 402
 * branch. It isn't part of the two-shapes-merged problem BE-004 fixed, so it keeps its own type.
 */
interface QuotaExceededBody {
  error?: string;
  errorCode?: string;
  quotaType?: string;
  currentPlan?: string;
  upgradeUrl?: string;
}

export class QuotaExceededError extends Error {
  status: number;
  details: unknown;

  constructor(message: string, details: unknown) {
    super(message);
    this.name = 'QuotaExceededError';
    this.status = 402;
    this.details = details;
  }
}

// Custom fetch wrapper with auth and error handling
class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    // CSRF double-submit: echo the readable csrf cookie on unsafe methods.
    const method = (options.method || 'GET').toUpperCase();
    const isUnsafe = !['GET', 'HEAD', 'OPTIONS'].includes(method);
    const csrfToken = isUnsafe ? getCsrfToken() : null;

    const config: RequestInit = {
      ...options,
      // Send the httpOnly auth cookie with every request -- the only credential mechanism the
      // app actually uses (FE-003: the Bearer-header fallback here used to read a token that
      // nothing in the app ever wrote to storage, so it was always absent in practice).
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken && { 'X-CSRF-Token': csrfToken }),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      // Handle auth errors - but don't redirect if we're already on login page
      if (response.status === 401) {
        const isLoginEndpoint = endpoint.includes('/Auth/login');
        
        // Read error data once
        const errorData: ApiErrorBody = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || 'Unauthorized';
        
        // Only redirect if we're not on the login endpoint
        if (!isLoginEndpoint) {
          if (errorMessage.toLowerCase().includes('organization') || 
              errorMessage.toLowerCase().includes('business') || 
              errorMessage.toLowerCase().includes('access') || 
              errorMessage.toLowerCase().includes('permission') ||
              errorMessage.toLowerCase().includes('belong')) {
            throw new ApiHttpError(errorMessage, response.status, errorData);
          }
          
          removeToken();
          removeUser();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        
        throw new ApiHttpError(errorMessage, response.status, errorData);
      }

      if (response.status === 403) {
        throw new Error('Forbidden');
      }

      if (response.status === 402) {
        const errorData: QuotaExceededBody = await response.json().catch(() => ({}));
        window.dispatchEvent(new CustomEvent('quota-exceeded', { detail: errorData }));
        throw new QuotaExceededError(errorData.error || 'Quota exceeded', errorData);
      }

      if (!response.ok) {
        const errorData: ApiErrorBody = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || `HTTP ${response.status}`;
        throw new ApiHttpError(errorMessage, response.status, errorData);
      }

      // 204/205 (and any other empty-body success, e.g. some DELETE endpoints)
      // have no JSON to parse -- response.json() throws SyntaxError on an
      // empty body, which every caller of delete()/post() with no expected
      // return value (revoke, remove, etc.) was silently treating as a
      // failure: the real 2xx result never reached the caller, so success
      // toasts/cache invalidation after those calls never ran.
      if (response.status === 204 || response.status === 205) {
        return undefined as T;
      }
      const rawBody = await response.text();
      const data: T = rawBody ? JSON.parse(rawBody) : (undefined as T);
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error');
    }
  }

  // GET request
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  // POST request
  async post<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PUT request
  async put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async getBlob(endpoint: string): Promise<Blob> {
    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, {
      credentials: 'include',
    });

    if (response.status === 401) {
      removeToken();
      removeUser();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      throw new ApiHttpError('Unauthorized', 401);
    }

    if (!response.ok) {
      const errorData: ApiErrorBody = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || `HTTP ${response.status}`;
      throw new ApiHttpError(errorMessage, response.status, errorData);
    }

    return response.blob();
  }
}

// Create API client instance
const apiClient = new ApiClient(API_BASE_URL);

// Auth API
export const authApi = {
  register: (data: {
    companyName: string;
    contactName: string;
    email: string;
    password: string;
    phoneNumber?: string;
    termsAccepted: boolean;
    kvkkConsentGiven: boolean;
  }) => apiClient.post('/Auth/register', data),

  login: (credentials: { email: string; password: string }) =>
    apiClient.post('/Auth/login', credentials),

  logout: () => apiClient.post('/Auth/logout'),

  setupPassword: (data: {
    email: string;
    password: string;
    confirmPassword: string;
    token: string;
  }) => apiClient.post(`/Auth/setup-password?token=${encodeURIComponent(data.token)}`, {
    email: data.email,
    password: data.password,
    confirmPassword: data.confirmPassword,
  }),
  
  validateSetupToken: (token: string) => 
    apiClient.get(`/Auth/validate-setup-token?token=${encodeURIComponent(token)}`),
  
  forgotPassword: (email: string) =>
    apiClient.post('/Auth/forgot-password', { email }),
  
  getCurrentUser: () => apiClient.get('/Auth/me'),
  
  updateProfile: (data: { name?: string; email?: string; theme?: string; language?: string }) =>
    apiClient.put('/Auth/profile', data),
  
  changePassword: (data: { currentPassword: string; newPassword: string; confirmPassword: string }) =>
    apiClient.put('/Auth/change-password', data),
};

// Organization API
export const invitationApi = {
  send: (data: { organizationId: string; email: string; organizationName: string }) =>
    apiClient.post('/Invitation', data),
  getPending: (organizationId: string) => apiClient.get(`/Invitation?organizationId=${organizationId}`),
  resend: (invitationId: string) => apiClient.post(`/Invitation/${invitationId}/resend`),
  revoke: (invitationId: string) => apiClient.delete(`/Invitation/${invitationId}`),
  accept: (token: string) => apiClient.post(`/Invitation/accept?token=${encodeURIComponent(token)}`),
  decline: (token: string) => apiClient.post(`/Invitation/decline?token=${encodeURIComponent(token)}`),
};

export const digestReportApi = {
  getSettings: (organizationId: string) => apiClient.get(`/DigestReport/settings/${organizationId}`),
  updateSettings: (organizationId: string, data: Record<string, unknown>) =>
    apiClient.put(`/DigestReport/settings/${organizationId}`, data),
  sendDailyNow: (organizationId: string) => apiClient.post(`/DigestReport/send-daily/${organizationId}`),
  sendWeeklyNow: (organizationId: string) => apiClient.post(`/DigestReport/send-weekly/${organizationId}`),
};

export const alertThresholdApi = {
  create: (data: Record<string, unknown>) => apiClient.post('/AlertThreshold', data),
  update: (thresholdId: string, data: Record<string, unknown>) =>
    apiClient.put(`/AlertThreshold/${thresholdId}`, data),
  remove: (thresholdId: string) => apiClient.delete(`/AlertThreshold/${thresholdId}`),
  getForOrganization: (organizationId: string) => apiClient.get(`/AlertThreshold/organizations/${organizationId}`),
  getHistory: (organizationId: string) => apiClient.get(`/AlertThreshold/organizations/${organizationId}/history`),
  checkNow: (organizationId: string) => apiClient.post(`/AlertThreshold/organizations/${organizationId}/check`),
  resolveAlert: (alertHistoryId: string) => apiClient.post(`/AlertThreshold/alerts/${alertHistoryId}/resolve`),
};

export const responseTemplateApi = {
  create: (data: Record<string, unknown>) => apiClient.post('/ResponseTemplate', data),
  update: (templateId: string, data: Record<string, unknown>) =>
    apiClient.put(`/ResponseTemplate/${templateId}`, data),
  remove: (templateId: string) => apiClient.delete(`/ResponseTemplate/${templateId}`),
  getForOrganization: (organizationId: string) => apiClient.get(`/ResponseTemplate/organizations/${organizationId}`),
  getGlobal: () => apiClient.get('/ResponseTemplate/global'),
  apply: (templateId: string, variables: Record<string, string>) =>
    apiClient.post(`/ResponseTemplate/${templateId}/apply`, { variables }),
  toggleFavorite: (templateId: string) => apiClient.post(`/ResponseTemplate/${templateId}/favorite`),
};

export const organizationApi = {
  getAll: () => apiClient.get('/Organization'),
  getById: (id: string) => apiClient.get(`/Organization/${id}`),
  create: (data: Record<string, unknown>) => apiClient.post('/Organization', data),
  update: (id: string, data: Record<string, unknown>) => apiClient.put(`/Organization/${id}`, data),
  delete: (id: string) => apiClient.delete(`/Organization/${id}`),
  getUsers: (orgId: string) => apiClient.get(`/Organization/${orgId}/users`),
  addUser: (orgId: string, data: Record<string, unknown>) =>
    apiClient.post(`/Organization/${orgId}/users`, data),
  removeUser: (orgId: string, userId: string) => apiClient.delete(`/Organization/${orgId}/users/${userId}`),
  getBusinesses: (orgId: string) => apiClient.get(`/Organization/${orgId}/businesses`),
  assignBusinesses: (orgId: string, userId: string, businessIds: string[]) => 
    apiClient.post(`/Organization/${orgId}/users/${userId}/businesses`, { businessIds }),
  removeBusinesses: (orgId: string, userId: string, businessIds: string[]) => 
    apiClient.delete(`/Organization/${orgId}/users/${userId}/businesses?businessIds=${businessIds.join(',')}`),
  getUserBusinesses: (orgId: string, userId: string) => 
    apiClient.get(`/Organization/${orgId}/users/${userId}/businesses`),
  markOnboardingComplete: (orgId: string) =>
    apiClient.post(`/Organization/${orgId}/onboarding-complete`),
  getUsage: (orgId: string) =>
    apiClient.get(`/Organization/${orgId}/usage`),
};

// Business API
export const businessApi = {
  getAll: (orgId: string) => apiClient.get(`/Organization/${orgId}/Business`),
  getById: (orgId: string, businessId: string) => apiClient.get(`/Organization/${orgId}/Business/${businessId}`),
  create: (orgId: string, data: BusinessInput) => apiClient.post(`/Organization/${orgId}/Business`, data),
  update: (orgId: string, businessId: string, data: BusinessInput) => {
    return apiClient.put(`/Organization/${orgId}/Business/${businessId}`, data);
  },
  delete: (orgId: string, businessId: string) => apiClient.delete(`/Organization/${orgId}/Business/${businessId}`),
  getReviews: (orgId: string, businessId: string) => apiClient.get(`/Organization/${orgId}/Business/${businessId}/Review`),
  syncReviews: (orgId: string, businessId: string) => apiClient.post(`/Organization/${orgId}/Business/${businessId}/sync`),
};

// Review API
export const reviewApi = {
  getReviews: (orgId: string, businessId: string, params?: ReviewListParams) => {
    const query = params
      ? new URLSearchParams(
          Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              acc[key] = String(value);
            }
            return acc;
          }, {})
        ).toString()
      : '';
    const suffix = query ? `?${query}` : '';
    return apiClient.get(`/Organization/${orgId}/Business/${businessId}/Review${suffix}`);
  },
  getById: (orgId: string, businessId: string, reviewId: string) => apiClient.get(`/Organization/${orgId}/Business/${businessId}/Review/${reviewId}`),
  getStatistics: (orgId: string, businessId: string) => apiClient.get(`/Organization/${orgId}/Business/${businessId}/Review/statistics`),
  getOrganizationStatistics: (orgId: string) => apiClient.get(`/Organization/${orgId}/Review/statistics`),
  analyzeBusiness: (orgId: string, businessId: string) => apiClient.post(`/Organization/${orgId}/Business/${businessId}/Review/analyze`),
  analyzeReview: (orgId: string, businessId: string, reviewId: string, userLanguage?: string) => 
    apiClient.post(`/Organization/${orgId}/Business/${businessId}/Review/${reviewId}/analyze`, { userLanguage }),
  analyzeReviewsBatch: (orgId: string, businessId: string, reviewIds: string[]) => 
    apiClient.post(`/Organization/${orgId}/Business/${businessId}/Review/analyze-batch`, { reviewIds }),
  getAnalysis: (orgId: string, businessId: string, reviewId: string) => apiClient.get(`/Organization/${orgId}/Business/${businessId}/Review/${reviewId}/analysis`),
  getRawData: (orgId: string, businessId: string, reviewId: string) => apiClient.get(`/Organization/${orgId}/Business/${businessId}/Review/${reviewId}/raw`),
  postReply: (reviewId: string, replyText: string) =>
    apiClient.post(`/ReviewResponse/reviews/${reviewId}/reply`, { replyText }),
  generateAiResponse: (reviewId: string, tone?: string) =>
    apiClient.post<{ content: string }>(`/ReviewResponse/reviews/${reviewId}/generate-ai-response`, { tone }),
  // Analytics endpoints
  getAnalytics: (orgId: string, businessId: string, periodDays: number = 30) => 
    apiClient.get(`/Organization/${orgId}/Business/${businessId}/ReviewAnalytics?periodDays=${periodDays}`),
  getTrend: (orgId: string, businessId: string, periodDays: number = 30) => 
    apiClient.get(`/Organization/${orgId}/Business/${businessId}/ReviewAnalytics/trend?periodDays=${periodDays}`),
  getTopics: (orgId: string, businessId: string, periodDays: number = 30) => 
    apiClient.get(`/Organization/${orgId}/Business/${businessId}/ReviewAnalytics/topics?periodDays=${periodDays}`),
  getInsights: (orgId: string, businessId: string, periodDays: number = 30) => 
    apiClient.get(`/Organization/${orgId}/Business/${businessId}/ReviewAnalytics/insights?periodDays=${periodDays}`),
  getKeywords: (orgId: string, businessId: string, periodDays: number = 30, limit: number = 20) => 
    apiClient.get(`/Organization/${orgId}/Business/${businessId}/ReviewAnalytics/keywords?periodDays=${periodDays}&limit=${limit}`),
};

// Platform API
export const platformApi = {
  getAvailable: (orgId: string, businessId: string) => 
    apiClient.get(`/Organization/${orgId}/Business/${businessId}/Platform/available`),
  getConnected: (orgId: string, businessId: string) => 
    apiClient.get(`/Organization/${orgId}/Business/${businessId}/Platform/connected`),
  connect: (orgId: string, businessId: string, platform: string, authCode?: string, redirectUri?: string) => {
    const normalizedPlatform = normalizePlatformSlug(platform);
    const url = `/Organization/${orgId}/Business/${businessId}/Platform/connect?platform=${encodeURIComponent(normalizedPlatform)}&authCode=${encodeURIComponent(authCode || '')}&redirectUri=${encodeURIComponent(redirectUri || '')}`;
    return apiClient.post(url);
  },
  disconnect: (orgId: string, businessId: string, platform: string) => {
    const normalizedPlatform = normalizePlatformSlug(platform);
    return apiClient.delete(`/Organization/${orgId}/Business/${businessId}/Platform/${normalizedPlatform}/disconnect`);
  },
  selectGoogleLocation: (businessId: string, accountId: string, locationId: string) => {
    return apiClient.post('/oauth/google/select-location', {
      businessId,
      accountId,
      locationId
    });
  },
  updateSettings: (orgId: string, businessId: string, platform: string, settings: Record<string, unknown>) => {
    const normalizedPlatform = normalizePlatformSlug(platform);
    return apiClient.put(`/Organization/${orgId}/Business/${businessId}/Platform/${normalizedPlatform}/settings`, settings);
  },
  updateCredentials: (orgId: string, businessId: string, platform: string, accessToken?: string, refreshToken?: string) => {
    const normalizedPlatform = normalizePlatformSlug(platform);
    return apiClient.put(`/Organization/${orgId}/Business/${businessId}/Platform/${normalizedPlatform}/credentials`, { accessToken, refreshToken });
  },
  getAuthUrl: (orgId: string, businessId: string, platform: string, redirectUri?: string) => {
    const normalizedPlatform = normalizePlatformSlug(platform);
    return apiClient.get(`/Organization/${orgId}/Business/${businessId}/Platform/auth-url?platform=${encodeURIComponent(normalizedPlatform)}&redirectUri=${encodeURIComponent(redirectUri || '')}`);
  },
  sync: (orgId: string, businessId: string, platform?: string) => {
    const normalizedPlatform = platform ? normalizePlatformSlug(platform) : 'all';
    return apiClient.post(`/Organization/${orgId}/Business/${businessId}/Platform/sync?platform=${normalizedPlatform}`);
  },
  getGoogleAccountsAndLocations: (orgId: string, businessId: string) =>
    apiClient.get(`/Organization/${orgId}/Business/${businessId}/Platform/google/accounts-locations`),
  connectGoogleBusiness: (orgId: string, businessId: string, accountId: string, locationId: string, locationName?: string) =>
    apiClient.post(`/Organization/${orgId}/Business/${businessId}/Platform/google/connect-business`, {
      accountId,
      locationId,
      locationName
    }),
};

// Subscription API
export const subscriptionApi = {
  getPlans: () => apiClient.get('/Subscription/plans'),
  getAll: (orgId: string) => apiClient.get(`/Subscription/organization/${orgId}/all`),
  getAllByOrganization: (orgId: string) => apiClient.get(`/Subscription/organization/${orgId}/all`),
  getById: (subscriptionId: string) => apiClient.get(`/Subscription/${subscriptionId}`),
  create: (data: Record<string, unknown>) => apiClient.post('/Subscription', data),
  update: (subscriptionId: string, data: Record<string, unknown>) =>
    apiClient.put(`/Subscription/${subscriptionId}`, data),
  cancel: (subscriptionId: string) => 
    apiClient.post(`/Subscription/${subscriptionId}/cancel`),
  reactivate: (subscriptionId: string) => 
    apiClient.post(`/Subscription/${subscriptionId}/reactivate`),
  checkStatus: (orgId: string) => apiClient.get(`/Subscription/organization/${orgId}/active`),
  checkActive: (orgId: string) => apiClient.get(`/Subscription/organization/${orgId}/active`),
  getExpiring: (daysAhead: number) => apiClient.get(`/Subscription/expiring?daysAhead=${daysAhead}`),
  getAllForAdmin: (page: number = 1, pageSize: number = 10) => 
    apiClient.get(`/Subscription/admin/all?page=${page}&pageSize=${pageSize}`),
  subscribe: (data: { planId: string; billingCycle?: string }) =>
    apiClient.post<{ checkoutFormContent: string }>('/Subscription/subscribe', data),
};

export const invoiceApi = {
  getByOrganization: (organizationId: string) =>
    apiClient.get(`/Invoice/organizations/${organizationId}`),
  downloadPdf: (invoiceId: string) => apiClient.getBlob(`/Invoice/${invoiceId}/download`),
};

export const apiKeysApi = {
  list: (orgId: string) => apiClient.get(`/organizations/${orgId}/api-keys`),
  create: (orgId: string, data: { label: string; scopes?: string[]; expiresAt?: string }) =>
    apiClient.post(`/organizations/${orgId}/api-keys`, data),
  revoke: (orgId: string, keyId: string) =>
    apiClient.delete(`/organizations/${orgId}/api-keys/${keyId}`),
};

export const agencyApi = {
  getClients: () => apiClient.get('/agency/clients'),
  switchContext: (targetOrganizationId: string) =>
    apiClient.post('/Auth/switch-context', { targetOrganizationId }),
  restoreContext: (agencyToken?: string) =>
    apiClient.post('/Auth/restore-context', agencyToken ? { agencyToken } : {}),
};

export const notificationApi = {
  getRecent: (_orgId: string) =>
    apiClient.get('/Notification/my-notifications?limit=20'),
  markRead: (_orgId: string, id: string) =>
    apiClient.post(`/Notification/${id}/mark-read`),
  markAllRead: (_orgId: string) =>
    apiClient.post('/Notification/mark-all-read'),
};

// Admin API
export const adminApi = {
  getPendingRegistrations: () => apiClient.get('/Admin/pending-registrations'),
  approveOrganization: (orgId: string) => apiClient.post(`/Admin/approve-organization/${orgId}`),
  getApplicationSettings: (configName: string) => apiClient.get(`/ApplicationSettings/${configName}`),
  createOrUpdateApplicationSettings: (configName: string, data: Record<string, unknown>) =>
    apiClient.put(`/ApplicationSettings/${configName}`, data),
  deleteApplicationSettings: (configName: string) => apiClient.delete(`/ApplicationSettings/${configName}`),
};

export default apiClient;